/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Box,
  Div,
  H5,
  P,
  SuccessToast,
  getLocationEarningPlan,
  lightTheme,
  updateLocationReferralAgentEarningPlan,
  reportError,
} from '@vgrubs/components';
import {
  LocationAgentServiceKey,
  UpdateLocationEarningsPlanRequestData,
} from 'components/src/types/api/location.types';
import {
  AgentDetails,
  EarningPlanEntity,
} from 'components/src/types/location.types';
import { useState } from 'react';
import { AddEarningPlan } from './AddEarningPlan';
import { AgentAvatars } from './AgentAvatars';
import { AddAgentToServiceModal } from './AddAgentToServiceModal';
import { AddLocationAgentModal } from './AddLocationAgentModal';
import { EarningPlans } from './EarningPlans';
import { EditEarningPlan } from './EditEarningPlan';
import { EditVDriveVOrderPlans } from './EditVDriveVOrderPlans';
import { LocationAgentsModal } from './LocationAgents';

export const Earnings = ({ referral, setLoading, id, agentsApi }) => {
  const [earningPlans, setEarningPlans] = useState<
    Partial<EarningPlanEntity>[]
  >([]);
  const [earning, setEarning] = useState<AgentDetails | null>(null);
  const [earningPlansModal, setEarningPlansModal] = useState(false);
  const [addEarningPlansModal, setAddEarningPlansModal] = useState(false);

  const [editVOrderPlans, setEditVOrderPlans] = useState(false);
  const [editVDrivePlans, setEditVDrivePlans] = useState(false);

  const [agentsModal, setAgentsModal] = useState(false);
  const [addAgentModal, setAddAgentModal] = useState(false);
  const [addToServiceModal, setAddToServiceModal] = useState<{
    key: LocationAgentServiceKey;
    label: string;
  } | null>(null);
  const [editPlanModal, setEditPlanModal] = useState<{
    plan: Partial<EarningPlanEntity>;
    index: number;
    label: string;
  } | null>(null);

  const {
    agentsData,
    agents,
    addAgent,
    deleteAgent,
    addAgentToService,
    removeAgentFromService,
    saveShares,
    refetch: refetchAgents,
  } = agentsApi;

  async function handleGetEarningPlans() {
    try {
      setLoading(true);
      const { data } = await getLocationEarningPlan(id);
      setEarningPlans(data.data.earningPlans);
      setEarning(data.data);
      setEarningPlansModal(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  // Persists vGrubs earning plans. Receives the already-updated list (state
  // updates are async) and refetches so new plans pick up their server ids.
  async function saveEarningPlans(plans: Partial<EarningPlanEntity>[]) {
    if (!earning?.vGrubsService) return;

    const ep = plans.map(({ id, year, amount }) => {
      return { id: id || null, year, amount: amount! };
    });

    const body: UpdateLocationEarningsPlanRequestData = {
      earningPlans: ep,
    };

    try {
      setLoading(true);
      const { data } = await updateLocationReferralAgentEarningPlan(id, body);
      SuccessToast(data.message);

      const { data: fresh } = await getLocationEarningPlan(id);
      setEarningPlans(fresh.data.earningPlans);
      setEarning(fresh.data);

      // The vGrubs total changed, so the per-service agent shares are stale.
      refetchAgents(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  // Per-service shares save: only the edited service carries amounts, the other
  // services are sent as null (skipped on the backend).
  const saveServiceShares = (
    service: LocationAgentServiceKey,
    editedAmounts: { agentId: number; amount: number }[]
  ) =>
    saveShares(
      editedAmounts.map(({ agentId, amount }) => ({
        agentId,
        vGrubsAmount: service === 'vGrubs' ? amount : null,
        vDriveAmount: service === 'vDrive' ? amount : null,
        vOrdersAmount: service === 'vOrders' ? amount : null,
      }))
    );

  // Edits a single vGrubs plan's amount / deletes it, then persists (the Edit
  // modal lives at this level so the Earning Plans modal can hide while it's open).
  const handleEditPlanAmount = (amount: number) => {
    if (!editPlanModal) return;
    const newPlans = earningPlans.map((plan, i) =>
      i === editPlanModal.index ? { ...plan, amount } : plan
    );
    setEarningPlans(newPlans);
    saveEarningPlans(newPlans);
  };

  const handleDeleteEditedPlan = () => {
    if (!editPlanModal) return;
    const newPlans = earningPlans.filter((_, i) => i !== editPlanModal.index);
    setEarningPlans(newPlans);
    saveEarningPlans(newPlans);
  };

  // Only the topmost modal is shown: a modal hides while one opened from it is
  // open, and reappears once that child is closed (its own state is preserved).
  const earningPlansChildOpen =
    agentsModal ||
    addToServiceModal !== null ||
    addEarningPlansModal ||
    editVOrderPlans ||
    editVDrivePlans ||
    editPlanModal !== null;

  const agentsChildOpen = addAgentModal;

  return (
    <>
      {/* Label over the avatars and the link centred on the right, the way
          the Owner card puts its arrow. On a card too narrow for both the
          link wraps below instead of squeezing the avatars. */}
      <Box $flex $wrap $between $alignCenter $gap="8px 20px">
        <Div>
          <P $mb="4">Agents</P>
          {agents.length > 0 ? (
            <AgentAvatars
              agents={agents}
              onShowAll={() => setAgentsModal(true)}
            />
          ) : (
            <H5 $mb="0">No Agents</H5>
          )}
        </Div>
        <P
          $m="0"
          $bold
          $underline
          $pointer
          $color={lightTheme.primary}
          onClick={
            agents.length !== 0
              ? handleGetEarningPlans
              : () => setAddAgentModal(true)
          }
        >
          <span>
            {agents.length !== 0 ? 'View Earning Plans' : 'Add Agents'}
          </span>
        </P>
      </Box>

      <LocationAgentsModal
        modalIsOpen={agentsModal && !agentsChildOpen}
        closeModal={() => setAgentsModal(false)}
        agents={agents}
        onAdd={() => setAddAgentModal(true)}
        onDelete={deleteAgent}
      />

      <EarningPlans
        modalIsOpen={earningPlansModal && !earningPlansChildOpen}
        closeModal={() => setEarningPlansModal(false)}
        earningPlans={earningPlans}
        earning={earning}
        setAddEarningPlansModal={setAddEarningPlansModal}
        onEditPlan={(plan, index, label) =>
          setEditPlanModal({ plan, index, label })
        }
        setEditVOrderPlans={setEditVOrderPlans}
        setEditVDrivePlans={setEditVDrivePlans}
        agents={agents}
        agentsData={agentsData}
        onAddAgentToService={(service) => setAddToServiceModal(service)}
        onDeleteAgentFromService={removeAgentFromService}
        onViewAgents={() => setAgentsModal(true)}
        onSaveShares={saveServiceShares}
      />

      <EditVDriveVOrderPlans
        modalIsOpen={editVOrderPlans}
        closeModal={() => setEditVOrderPlans(false)}
        planAmount={earning?.vOrdersCommision || 0}
        type="vTablet"
        setLoading={setLoading}
        id={id}
        setEarning={setEarning}
        onSaved={() => refetchAgents(true)}
      />

      <EditVDriveVOrderPlans
        modalIsOpen={editVDrivePlans}
        closeModal={() => setEditVDrivePlans(false)}
        planAmount={earning?.vDriveCommission || 0}
        type="vDrive"
        setLoading={setLoading}
        id={id}
        setEarning={setEarning}
        onSaved={() => refetchAgents(true)}
      />

      <AddEarningPlan
        modalIsOpen={addEarningPlansModal}
        closeModal={() => setAddEarningPlansModal(false)}
        earningPlans={earningPlans}
        setEarningPlans={setEarningPlans}
        onSavePlans={saveEarningPlans}
      />

      <EditEarningPlan
        modalIsOpen={!!editPlanModal}
        closeModal={() => setEditPlanModal(null)}
        planAmount={editPlanModal?.plan.amount || 0}
        label={editPlanModal?.label || ''}
        handleSetAmount={handleEditPlanAmount}
        handleDeletePlan={handleDeleteEditedPlan}
      />

      <AddLocationAgentModal
        modalIsOpen={addAgentModal}
        closeModal={() => setAddAgentModal(false)}
        existingUserIds={agents.map((agent) => agent.userId)}
        onConfirm={addAgent}
      />

      <AddAgentToServiceModal
        service={addToServiceModal}
        closeModal={() => setAddToServiceModal(null)}
        agents={agents}
        onConfirm={addAgentToService}
        onAddToLocation={() => {
          setAddToServiceModal(null);
          setAddAgentModal(true);
        }}
      />
    </>
  );
};
